import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Badge from '@orn/shared-web/components/Badge/Badge';
import Button from '@orn/shared-web/components/Button/Button';
import MessagingCard from '@orn/shared-web/components/MessagingCard';

import { useFeatureGates } from '../../../hooks/useFeatureGates';
import CheckoutFlow from '../../billing/CheckoutFlow';
import FreeTrialInfo from '../../FreeTrialInfo';
import LegalLinks from '../../LegalLinks';
import { OnboardingTitle } from '../../OnboardingLayout';

import type { IOnboardingStepProps } from '../types';

const ANNUAL_PRICE_LABEL = '$99/year';
const ANNUAL_PRICE_SUBTITLE = 'after your 30-day free trial';

/**
 * Onboarding payment screen.
 *
 * Behind the billing gate this is the checkout flow — plan, account,
 * authorize, with a card-entry divert. While the gate is closed it stays
 * the explainer it has always been: `eligibleBillingAccounts` refuses an ungated caller outright,
 * so rendering checkout would break the terminal onboarding step for everyone
 * who cannot use it.
 */
const PaymentStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { flags, loading: gatesLoading } = useFeatureGates();
  const billingEnabled = flags.has(FeatureGate.BillingCheckout);

  if (gatesLoading) {
    // The gate answer decides whether this terminal step is checkout or the
    // legacy explainer; rendering the explainer's active CTA early would let
    // a billing-enabled household finish onboarding without subscribing.
    return (
      <div className="flex w-full flex-col gap-6">
        <OnboardingTitle
          title="Start your free trial"
          subtitle="Try Mantle free for 30 days. Cancel anytime."
          onBack={onBack}
        />
        <p className="text-tertiary text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <OnboardingTitle
        title="Start your free trial"
        subtitle="Try Mantle free for 30 days. Cancel anytime."
        onBack={onBack}
      />

      {billingEnabled ? (
        <CheckoutFlow onComplete={onNext} />
      ) : (
        <>
          <div className="flex w-full flex-col gap-6">
            <MessagingCard variant="brand" className="flex w-full flex-col items-center justify-center gap-2">
              <p className="text-text-primary text-display-xs font-display font-medium">{ANNUAL_PRICE_LABEL}</p>
              <p className="text-text-tertiary text-sm">{ANNUAL_PRICE_SUBTITLE}</p>
            </MessagingCard>

            <FreeTrialInfo className="w-full" />
          </div>

          <div className="flex w-full flex-col items-center gap-5">
            <Badge size="lg" color="brand" fill="solid" className="text-md px-4 py-1.5">
              30 day free trial, cancel anytime
            </Badge>
            <Button type="button" size="xl" color="quaternary" className="w-full" onClick={onNext}>
              Try for $0.00
            </Button>
            <p className="text-tertiary text-xs">
              By starting your trial, you agree to Mantle&apos;s <LegalLinks />.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

PaymentStep.displayName = 'PaymentStep';

export default PaymentStep;
